import { useState } from 'react';
import type { NeedSummary, VendorWithRelations } from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { useVendors } from '../../hooks/useVendors';
import { useBookNeed } from '../../hooks/useNeeds';
import { readPrice } from '../expenses/NeedForm';
import { QuickVendorForm } from './QuickVendorForm';
import { extractApiErrorMessage } from '../../utils/apiError';

export interface BookNeedModalProps {
  need: NeedSummary | null;
  onClose: () => void;
}

const INPUT =
  'w-full h-11 sm:h-10 px-3 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500';

/**
 * Book a need with nothing to shop for — the venue's own caterer, the vendor
 * used every time: the vendor and price straight away, no quotes.
 */
export function BookNeedModal({ need, onClose }: BookNeedModalProps) {
  return (
    <EntityModal open={need !== null} onClose={onClose} title="Book directly" size="md">
      {need && <BookNeedForm key={need.id} need={need} onClose={onClose} />}
    </EntityModal>
  );
}

function BookNeedForm({ need, onClose }: { need: NeedSummary; onClose: () => void }) {
  const { data: vendors = [], isLoading } = useVendors({ isActive: true });
  const book = useBookNeed(need.id);
  const [vendorId, setVendorId] = useState<string | null>(null);
  const [added, setAdded] = useState<VendorWithRelations[]>([]);
  const [price, setPrice] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [newVendor, setNewVendor] = useState(false);
  const [error, setError] = useState('');

  const options = [...added.filter((a) => !vendors.some((v) => v.id === a.id)), ...vendors];
  const amount = readPrice(price);

  const save = async () => {
    setError('');
    if (!vendorId) return setError('Choose the vendor this is booked with.');
    if (amount === null || amount === 'invalid') return setError('Enter the price as a number.');
    try {
      await book.mutateAsync({ vendorId, price: amount, dueDate: dueDate || null });
      onClose();
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Could not book it'));
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Book <span className="font-medium text-gray-900">{need.itemName || 'this need'}</span> with
        a vendor at an agreed price, without asking for quotes.
      </p>
      {error && <div className="px-3 py-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>}
      <div>
        <label htmlFor="book-need-vendor" className="block text-sm font-medium text-gray-700 mb-1">
          Vendor
        </label>
        <SearchableListSelect
          inputId="book-need-vendor"
          items={options}
          value={vendorId}
          onChange={(v) => setVendorId((v as string) || null)}
          getValue={(v) => v.id}
          getLabel={(v) => v.businessName}
          getSubtitle={(v) => v.categories.map((c) => c.categoryName).join(', ') || null}
          isLoading={isLoading}
          placeholder="Search vendors"
          ariaLabel="Vendor"
        />
        {!newVendor && (
          <button
            type="button"
            onClick={() => setNewVendor(true)}
            className="mt-1 min-h-11 sm:min-h-0 text-sm text-primary-700 hover:underline"
          >
            + New vendor
          </button>
        )}
      </div>
      {newVendor && (
        <QuickVendorForm
          defaultCategoryId={need.vendorCategoryId}
          onCancel={() => setNewVendor(false)}
          onCreated={(vendor) => {
            setAdded((list) => [vendor, ...list]);
            setVendorId(vendor.id);
            setNewVendor(false);
          }}
        />
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="book-need-price" className="block text-sm font-medium text-gray-700 mb-1">
            Price
          </label>
          <input
            id="book-need-price"
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            className={INPUT}
          />
        </div>
        <div>
          <label htmlFor="book-need-due" className="block text-sm font-medium text-gray-700 mb-1">
            Due date <span className="text-xs text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            id="book-need-due"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className={INPUT}
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="min-h-11 sm:min-h-9 px-4 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={book.isPending}
          className="min-h-11 sm:min-h-9 px-4 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {book.isPending ? 'Booking…' : 'Book'}
        </button>
      </div>
    </div>
  );
}
